import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, CircularProgress
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { User } from "./userTypes";

interface Props {
  open: boolean;
  onClose: () => void;
  editLoading: boolean;
  selectedUser: User | null;
  editData: Partial<User>;
  onFieldChange: (field: keyof User, value: string | boolean) => void;
  onSave: () => void;
  canWrite: boolean;
  onGoToDetails: (id: number) => void;
}

/** Quick view + inline edit of a user (opened from the list). */
const UserQuickViewModal: React.FC<Props> = ({
  open, onClose, editLoading, selectedUser, editData, onFieldChange, onSave, canWrite, onGoToDetails,
}) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>User Quick View</DialogTitle>
      <DialogContent>
        {editLoading ? (
            <CircularProgress />
        ) : selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Visibility />}
                    onClick={() => onGoToDetails(selectedUser.id)}
                >
                  Go to Full Details Page
                </Button>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2, mt: 1 }}>
                <TextField label="ID" value={selectedUser.id} fullWidth disabled />
                <TextField label="Firebase UID" value={selectedUser.firebaseUid ?? ""} fullWidth disabled />
                <TextField label="Created At" value={selectedUser.createdAt || ""} fullWidth disabled />
                <TextField label="Updated At" value={selectedUser.updatedAt || ""} fullWidth disabled />
                <TextField label="FCM Token" value={selectedUser.fcmToken ?? ""} fullWidth disabled />
                <TextField label="Name" value={editData.name ?? ""} fullWidth onChange={e => onFieldChange("name", e.target.value)} />
                <TextField label="Email" value={editData.email ?? ""} fullWidth onChange={e => onFieldChange("email", e.target.value)} />
                <TextField label="Phone" value={editData.phone ?? ""} fullWidth onChange={e => onFieldChange("phone", e.target.value)} />
                <TextField label="Gender" value={editData.gender ?? ""} fullWidth onChange={e => onFieldChange("gender", e.target.value)} />
                <TextField label="Assistance Required" value={editData.assistanceRequired ?? ""} fullWidth onChange={e => onFieldChange("assistanceRequired", e.target.value)} />
                <TextField label="Auth Code" value={editData.authCode ?? ""} fullWidth onChange={e => onFieldChange("authCode", e.target.value)} />
                <TextField select label="Suspended" value={editData.suspended ? "yes" : "no"} fullWidth onChange={e => onFieldChange("suspended", e.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField select label="Phone Verified" value={editData.phoneVerified ? "yes" : "no"} fullWidth onChange={e => onFieldChange("phoneVerified", e.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
                <TextField select label="Email Verified" value={editData.emailVerified ? "yes" : "no"} fullWidth onChange={e => onFieldChange("emailVerified", e.target.value === "yes")}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </TextField>
              </Box>
            </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canWrite && (
          <Button variant="contained" onClick={onSave} disabled={editLoading}>Save</Button>
        )}
      </DialogActions>
    </Dialog>
);

export default UserQuickViewModal;
